import { Injectable, signal } from '@angular/core';
import type { DocumentMgmtDragPayload } from './drag-payload';

/**
 * Process-wide signal carrying the in-flight drag payload.
 *
 * Why this exists: HTML5 drag-and-drop intentionally hides `DataTransfer.getData()` during
 * `dragenter` / `dragover` / `dragleave` for cross-origin security — only `types` is exposed at
 * those phases. Without an out-of-band channel, our `dragover` handler can't validate the
 * target against the source (self-drop, same-folder, descendant cycle) without going through
 * the actual drop, by which time `preventDefault()` is too late to allow the drop.
 *
 * The service is the out-of-band channel: `start()` is called from `dragstart` (where `setData`
 * still works for the drop handler that reads the official payload), and the same payload is
 * mirrored here so dragover/leave/highlight handlers can read it back.
 *
 * Singleton-scoped (`providedIn: 'root'`) because the source and target rows can live in
 * different `FolderColumnComponent` instances within one folder-browser tree.
 */
@Injectable({ providedIn: 'root' })
export class DocumentMgmtDragService {
  private readonly activeDragSignal = signal<DocumentMgmtDragPayload | null>(null);
  /** Read-only handle for components rendering drag-aware affordances. */
  readonly activeDrag = this.activeDragSignal.asReadonly();

  start(payload: DocumentMgmtDragPayload): void {
    this.activeDragSignal.set(payload);
  }

  clear(): void {
    this.activeDragSignal.set(null);
  }
}
